/**
 * Chat Service - Handles streaming chat requests and message updates
 */

import { StreamEventType, parseSSEData } from '../lib/sse-utils.js';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

/**
 * @typedef {function(string): void} MessageUpdater
 */

// ============================================================================
// NETWORK
// ============================================================================

/**
 * Sends a streaming chat request using fetch
 * @param {object} request - Chat request object
 * @param {string} apiBaseUrl - Base URL for the API
 */
export const sendStreamingRequest = async (request, apiBaseUrl, organizationId) => {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream'
      };
    
      // Add organization ID header if provided
      if (organizationId) {
        headers['x-eshal-org'] = organizationId;
      }


      const response =  await fetch(`${apiBaseUrl.replace(/\/$/, '')}/api/v1/support`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(request),
      });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  if (!response.body) {
    throw new Error('Response body is null');
  }

  return response.body;
};

// ============================================================================
// UI HELPERS
// ============================================================================

/**
 * Creates a message updater function with optimized batching (~60fps)
 * @param {function} setMessages - React state setter for messages
 * @returns {MessageUpdater} - Message updater function
 */
export const createMessageUpdater = (setMessages) => {
  let pendingUpdate = null;
  let rafId = null;
  let lastUpdateTime = 0;

  const flushUpdate = (timestamp) => {
    if (timestamp - lastUpdateTime < 16) {
      rafId = requestAnimationFrame(flushUpdate);
      return;
    }

    if (pendingUpdate !== null) {
      const contentToUpdate = pendingUpdate;
      pendingUpdate = null;
      lastUpdateTime = timestamp;

      setMessages((prev) => {
        const updatedMessages = [...(prev || [])];
        const lastMessage = updatedMessages[updatedMessages.length - 1];

        if (lastMessage?.role === 'assistant') {
          updatedMessages[updatedMessages.length - 1] = {
            ...lastMessage,
            content: contentToUpdate,
          };
        }

        return updatedMessages;
      });
    }

    rafId = null;
  };

  return (content) => {
    pendingUpdate = content;
    if (rafId === null) {
      rafId = requestAnimationFrame(flushUpdate);
    }
  };
};

/**
 * Creates a skeleton message for processing state
 * @returns {object} - Skeleton message object
 */
export const createSkeletonMessage = () => ({
  id: `skeleton-${Date.now()}`,
  role: 'assistant',
  content: '',
  timestamp: new Date(),
  isProcessing: true,
});

/**
 * Updates the last message to remove processing state
 * @param {function} setMessages - React state setter for messages
 */
export const removeProcessingState = (setMessages) => {
  setMessages((prev) => {
    const updatedMessages = [...(prev || [])];
    const lastMessage = updatedMessages[updatedMessages.length - 1];

    if (lastMessage?.isProcessing) {
      updatedMessages[updatedMessages.length - 1] = {
        ...lastMessage,
        isProcessing: false,
      };
    }

    return updatedMessages;
  });
};

/**
 * Updates the last assistant message with final content
 * @param {function} setMessages - React state setter for messages
 * @param {string} content - Final content to set
 */
export const updateFinalMessage = (setMessages, content) => {
  setMessages((prev) => {
    const updatedMessages = [...(prev || [])];
    const lastMessage = updatedMessages[updatedMessages.length - 1];

    if (lastMessage?.role === 'assistant') {
      updatedMessages[updatedMessages.length - 1] = {
        ...lastMessage,
        content,
        isProcessing: false,
      };
    }

    return updatedMessages;
  });
};

/**
 * Adds error content to the last assistant message
 * @param {function} setMessages - React state setter for messages
 * @param {string} error - Error message to add
 */
export const addErrorMessage = (setMessages, error) => {
  setMessages((prev) => {
    const updatedMessages = [...(prev || [])];
    const lastMessage = updatedMessages[updatedMessages.length - 1];

    if (lastMessage?.role === 'assistant') {
      updatedMessages[updatedMessages.length - 1] = {
        ...lastMessage,
        content: (lastMessage.content || '') + `\n\nError: ${error}`,
        isProcessing: false,
      };
    }

    return updatedMessages;
  });
};

// ============================================================================
// STREAM PROCESSING
// ============================================================================

/**
 * Handles different types of streaming events
 * @param {object} streamEvent - Parsed stream event
 * @param {function} setMessages - React state setter for messages
 * @param {object} accumulatedText - Mutable object to store accumulated text
 * @param {MessageUpdater} updateMessage - Message updater function
 * @param {object} hasReceivedFirstChunk - Mutable object to track first chunk
 */
export const handleStreamEvent = (
  streamEvent,
  setMessages,
  accumulatedText,
  updateMessage,
  hasReceivedFirstChunk
) => {
  if (!streamEvent || !streamEvent.type) {
    return;
  }

  switch (streamEvent.type) {
    case StreamEventType.PROCESSING_START:
      // Ignore processing_start events - don't create messages for them
      // We'll show typing indicator instead until first text_chunk arrives
      break;

    case StreamEventType.TEXT_CHUNK:
      // Create skeleton message only when first text chunk arrives
      if (!hasReceivedFirstChunk.current) {
        hasReceivedFirstChunk.current = true;
        setMessages((prev) => {
          const lastMessage = prev[prev.length - 1];
          // Create skeleton if no messages exist, or last message is not a processing assistant message
          if (!lastMessage || (lastMessage.role !== 'assistant' || !lastMessage.isProcessing)) {
            const skeletonMessage = createSkeletonMessage();
            return [...(prev || []), skeletonMessage];
          }
          return prev;
        });
      }

      // Extract content from either content field or OpenAI format choices[0].delta.content
      const chunkContent = streamEvent.content || streamEvent.choices?.[0]?.delta?.content || '';
      if (chunkContent) {
        accumulatedText.current += chunkContent;
        updateMessage(accumulatedText.current);
      }
      break;

    case StreamEventType.PROCESSING_END:
      removeProcessingState(setMessages);
      break;

    case StreamEventType.COMPLETE: {
      const finalContent = streamEvent.finalContent || accumulatedText.current;
      updateFinalMessage(setMessages, finalContent);
      break;
    }

    case StreamEventType.ERROR:
      addErrorMessage(setMessages, streamEvent.error || 'Unknown error occurred');
      break;

    case StreamEventType.HUMAN_DECISION_REQUIRED: {
      const decisionMessage = {
        id: `decision-${Date.now()}`,
        role: 'assistant',
        content: streamEvent.question || '',
        timestamp: new Date(),
        isProcessing: false,
        requiresDecision: true,
        decisionData: {
          decisionId: streamEvent.decisionId,
          question: streamEvent.question,
          options: streamEvent.options || [],
          context: streamEvent.context || {},
        },
      };

      setMessages((prev) => {
        const filtered = (prev || []).filter(
          (msg) => !msg.isProcessing || msg.content
        );
        const updated = [...filtered, decisionMessage];
        return updated;
      });
      break;
    }

    default:
      // Unknown event type, log for debugging
      console.warn('Unknown stream event type:', streamEvent.type);
      break;
  }
};

/**
 * Processes a readable stream and handles SSE events
 * @param {ReadableStream<Uint8Array>} stream - The response stream
 * @param {function} setMessages - React state setter for messages
 */
export const processStream = async (stream, setMessages) => {
  const updateMessage = createMessageUpdater(setMessages);
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const accumulatedText = { current: '' };
  const hasReceivedFirstChunk = { current: false };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const segments = buffer.split(/\r?\n\r?\n/);
      buffer = segments.pop() || '';

      for (const segment of segments) {
        const lines = segment
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean);

        if (lines.length === 0) {
          continue;
        }

        const dataLines = lines
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trim());

        if (dataLines.length === 0) {
          continue;
        }

        const payload = dataLines.join('\n');

        if (payload === '[DONE]') {
          continue;
        }

        // Parse the payload
        let parsed;
        try {
          parsed = JSON.parse(payload);
        } catch (e) {
          // Not valid JSON, skip
          continue;
        }

        if (!parsed) {
          continue;
        }

        // Handle events with type field (new format with type + OpenAI structure)
        if (parsed.type) {
          // Ignore processing_start and processing_end events - don't display their content
          if (parsed.type === 'processing_start' || parsed.type === 'processing_end') {
            continue;
          }

          // Handle text_chunk events
          if (parsed.type === 'text_chunk') {
            const content = parsed.choices?.[0]?.delta?.content || parsed.content;
            if (content) {
              // Create skeleton on first chunk if not already created
              if (!hasReceivedFirstChunk.current) {
                hasReceivedFirstChunk.current = true;
                setMessages((prev) => {
                  const lastMessage = prev[prev.length - 1];
                  if (!lastMessage || (lastMessage.role !== 'assistant' || !lastMessage.isProcessing)) {
                    const skeletonMessage = createSkeletonMessage();
                    return [...(prev || []), skeletonMessage];
                  }
                  return prev;
                });
              }

              accumulatedText.current += content;
              updateMessage(accumulatedText.current);
            }
            continue;
          }

          // Handle complete events
          if (parsed.type === 'complete') {
            // Finalize the message
            const finalContent = parsed.finalContent || accumulatedText.current;
            if (finalContent) {
              setMessages((prev) => {
                const updatedMessages = [...(prev || [])];
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                if (lastMessage?.role === 'assistant') {
                  updatedMessages[updatedMessages.length - 1] = {
                    ...lastMessage,
                    content: finalContent,
                    isProcessing: false,
                  };
                }
                return updatedMessages;
              });
            }
            // Remove processing state
            setMessages((prev) => {
              const updatedMessages = [...(prev || [])];
              const lastMessage = updatedMessages[updatedMessages.length - 1];
              if (lastMessage?.isProcessing) {
                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  isProcessing: false,
                };
              }
              return updatedMessages;
            });
            continue;
          }

          // Handle error events
          if (parsed.type === 'error') {
            setMessages((prev) => {
              const updatedMessages = [...(prev || [])];
              const lastMessage = updatedMessages[updatedMessages.length - 1];
              if (lastMessage?.role === 'assistant') {
                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  content: (lastMessage.content || '') + `\n\nError: ${parsed.error || 'Unknown error occurred'}`,
                  isProcessing: false,
                };
              }
              return updatedMessages;
            });
            continue;
          }

          // Try to parse as stream event using handleStreamEvent
          const streamEvent = parseSSEData(payload);
          if (streamEvent && streamEvent.type) {
            console.log('🔍 Parsed stream event:', streamEvent.type, streamEvent);
            handleStreamEvent(
              streamEvent,
              setMessages,
              accumulatedText,
              updateMessage,
              hasReceivedFirstChunk
            );
            continue;
          }
        }

        // Handle OpenAI format without type field (fallback)
        if (parsed.object === 'chat.completion.chunk' && parsed.choices?.[0]?.delta?.content) {
          // Create skeleton on first chunk if not already created
          if (!hasReceivedFirstChunk.current) {
            hasReceivedFirstChunk.current = true;
            setMessages((prev) => {
              const lastMessage = prev[prev.length - 1];
              if (!lastMessage || (lastMessage.role !== 'assistant' || !lastMessage.isProcessing)) {
                const skeletonMessage = createSkeletonMessage();
                return [...(prev || []), skeletonMessage];
              }
              return prev;
            });
          }

          accumulatedText.current += parsed.choices[0].delta.content;
          updateMessage(accumulatedText.current);
          continue;
        }

        // Handle finish_reason (stream complete)
        if (parsed.choices?.[0]?.finish_reason && parsed.choices[0].finish_reason !== null) {
          setMessages((prev) => {
            const updatedMessages = [...(prev || [])];
            const lastMessage = updatedMessages[updatedMessages.length - 1];
            if (lastMessage?.isProcessing) {
              updatedMessages[updatedMessages.length - 1] = {
                ...lastMessage,
                isProcessing: false,
              };
            }
            return updatedMessages;
          });
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
};